import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Headphones, Plus, Ticket, LogIn } from 'lucide-react';
import { api } from '../../lib/api';
import { useAuthStore } from '../../stores/authStore';
import { TicketCard, TicketForm } from '../../components/support';
import { SupportTicket, TicketStatus } from '@outlaw-oasis/types';

type TabType = 'tickets' | 'create';

const statusFilters: { value: TicketStatus | 'all'; label: string }[] = [
  { value: 'all', label: 'All Tickets' },
  { value: 'open', label: 'Open' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'waiting_customer', label: 'Awaiting Reply' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'closed', label: 'Closed' },
];

export default function Support() {
  const { isAuthenticated } = useAuthStore();
  const [activeTab, setActiveTab] = useState<TabType>('tickets');
  const [statusFilter, setStatusFilter] = useState<TicketStatus | 'all'>('all');

  const { data, isLoading } = useQuery({
    queryKey: ['my-tickets', statusFilter],
    queryFn: () => api.get(`/support/tickets?status=${statusFilter}`),
    enabled: isAuthenticated,
  });

  const tickets: SupportTicket[] = (data as any)?.data?.tickets || [];

  // If not authenticated, show login prompt
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-charcoal">
        <div className="max-w-4xl mx-auto px-4 py-12">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gold/20 mb-4">
              <Headphones className="w-8 h-8 text-gold" />
            </div>
            <h1 className="text-3xl font-western text-gold mb-2">Support Center</h1>
            <p className="text-text-secondary">
              Get help from our support team
            </p>
          </div>

          <div className="bg-surface rounded-xl border border-gold/20 p-8 text-center">
            <LogIn className="w-12 h-12 text-gold mx-auto mb-4" />
            <h2 className="text-xl font-medium text-cream mb-2">Sign In Required</h2>
            <p className="text-text-secondary mb-6">
              Please sign in to create support tickets and view your ticket history.
            </p>
            <Link
              to="/login?redirect=/support"
              className="inline-flex items-center gap-2 px-6 py-3 bg-gold text-leather font-medium rounded-lg hover:bg-gold-bright transition-colors"
            >
              Sign In to Continue
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-charcoal">
      <div className="max-w-4xl mx-auto px-4 py-12">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gold/20 mb-4">
            <Headphones className="w-8 h-8 text-gold" />
          </div>
          <h1 className="text-3xl font-western text-gold mb-2">Support Center</h1>
          <p className="text-text-secondary">
            Need help? Create a ticket and we'll get back to you.
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setActiveTab('tickets')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === 'tickets'
                ? 'bg-gold text-leather'
                : 'bg-surface border border-gold/20 text-cream hover:border-gold/40'
            }`}
          >
            <Ticket className="w-4 h-4" />
            My Tickets
          </button>
          <button
            onClick={() => setActiveTab('create')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === 'create'
                ? 'bg-gold text-leather'
                : 'bg-surface border border-gold/20 text-cream hover:border-gold/40'
            }`}
          >
            <Plus className="w-4 h-4" />
            New Ticket
          </button>
        </div>

        {/* Content */}
        {activeTab === 'create' ? (
          <div className="bg-surface rounded-xl border border-gold/20 p-6">
            <h2 className="text-xl font-western text-gold mb-6">Create Support Ticket</h2>
            <TicketForm />
          </div>
        ) : (
          <div className="space-y-6">
            {/* Status Filter */}
            <div className="flex flex-wrap gap-2">
              {statusFilters.map((filter) => (
                <button
                  key={filter.value}
                  onClick={() => setStatusFilter(filter.value)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    statusFilter === filter.value
                      ? 'bg-gold/20 text-gold border border-gold/40'
                      : 'bg-leather border border-gold/20 text-text-secondary hover:border-gold/40'
                  }`}
                >
                  {filter.label}
                </button>
              ))}
            </div>

            {/* Tickets List */}
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" />
              </div>
            ) : tickets.length === 0 ? (
              <div className="bg-surface rounded-xl border border-gold/20 p-8 text-center">
                <Ticket className="w-12 h-12 text-text-muted mx-auto mb-4" />
                <h3 className="text-lg font-medium text-cream mb-2">No tickets found</h3>
                <p className="text-text-secondary mb-4">
                  {statusFilter === 'all'
                    ? "You haven't created any support tickets yet."
                    : `You don't have any tickets with this status.`}
                </p>
                <button
                  onClick={() => setActiveTab('create')}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-gold text-leather font-medium rounded-lg hover:bg-gold-bright transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  Create Your First Ticket
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                {tickets.map((ticket) => (
                  <TicketCard key={ticket.id} ticket={ticket} />
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
